import React, { useCallback, useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { ValidationRule, ButtonType } from 'devextreme-react/common';
import { Popup } from 'devextreme-react/popup';
import { PopupPosition } from '../common/PopupPositions';
import { formatMessage } from 'devextreme/localization';
import { Form, Label, FormRef, GroupItem, SimpleItem, ButtonItem, EmptyItem } from 'devextreme-react/form';
import 'devextreme-react/text-area';

import { useAuth } from '../../contexts/auth';

import DataSourceForm, { DataSourceFormHandle } from '../data-source-form/DataSourceForm';

import LabelTemplate from '../label-template/LabelTemplate';

import DataSourceApi from "../../api/DataSourceApi";

import DataProviderType from '../../classes/consts/DataProviderType';

import DataSourceListDto from '../../classes/dtos/datasourcemodule/DataSourceListDto';
import PathListDto from '../../classes/dtos/pathmodule/PathListDto';
import DataProviderListDto from '../../classes/dtos/datasourcemodule/DataProviderListDto';

import DataSourceSelectionDto from '../../classes/dtos/datasourcemodule/DataSourceSelectionDto';


interface DataSourceSelectionFormProps {
    Direction: string;
    WithDataTable: boolean;
    WithCreate: boolean;
    SetDataSourceSelected(value: DataSourceSelectionDto): void;
}
export interface DataSourceSelectionFormHandle {
    Open: (dataProviderId: number, dataProviderType: string) => void;
}

const DataSourceSelectionForm = forwardRef<DataSourceSelectionFormHandle, DataSourceSelectionFormProps>(({ Direction, WithDataTable, WithCreate, SetDataSourceSelected }, ref) => {

    const dataSourceFormHandleRef = useRef<DataSourceFormHandle>(null);

    const [dataSourceSelectionDto, setDataSourceSelectionDto] = useState<DataSourceSelectionDto>(new DataSourceSelectionDto(null));

    const [dataSourceId, setDataSourceId] = useState<number>(-1);

    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
        Open(dataProviderId: number, dataProviderType: string) {
            if (dataProviders !== null) {
                let current = dataProviders.find((e) => e.Id === dataProviderId && e.Type === dataProviderType) ?? null;
                setDataSourceSelectionDto(new DataSourceSelectionDto(current));
            }
            setIsOpen(true);
        }
    }));

    function editDataSource(): void {
        if (dataSources !== null) {
            let current = dataSources.find((e) => e.Id === dataSourceSelectionDto.DataProvider?.Id) ?? null;
            if (current !== null) {
                if (dataSourceFormHandleRef.current !== null) {
                    dataSourceFormHandleRef.current.Open(current.Id);
                }
            }
        }
    };

    function createDataSource(): void {
        if (dataSourceFormHandleRef.current !== null) {
            dataSourceFormHandleRef.current.Open(null);
        }
    };

    function cancel(): void {
        setIsOpen(false);
    };

    const formRef = useRef<FormRef>(null);

    const [dataSources, setDataSources] = useState<DataSourceListDto[] | null>(null);
    const [pathDataTables, setPathDataTables] = useState<PathListDto[] | null>(null);

    const [dataProviders, setDataProviders] = useState<DataProviderListDto[] | null>(null);

    const [currentUserId] = useState<number>(useAuth().user?.UserId ?? 0);

    const [currentDataSource, setCurrentDataSource] = useState<DataSourceListDto | null>(null);

    const dataSourceEditorOptions = {
        items: dataProviders,
        searchEnabled: true,
        displayExpr: "Name",
        onValueChanged: (e: any) => {
            if (e.value.Type === DataProviderType.DataSource) {
                let dataSource = dataSources?.find(x => x.Id === e.value.Id) ?? null;
                setCurrentDataSource(dataSource);
            }
            else {
                setCurrentDataSource(null);
            }
        },
        buttons: [{
            name: 'edit',
            location: 'after',
            options: {
                stylingMode: 'text',
                icon: 'edit',
                onClick: () => {
                    editDataSource();
                },
                visible: currentDataSource !== null,
                disabled: currentDataSource?.CreatedBy.Id !== currentUserId
            },
        }]
    };

    const dataSourcesLoad = useCallback(async () => {
        if (!dataSources || !pathDataTables) {
            let readable = Direction === "Source" ? true : null;
            let writable = Direction === "Target" ? true : null;

            DataSourceApi.GetDataSourcesByCustomerAsync(readable, writable)
                .then(getDataSourcesResult => {
                    let allDataSources = getDataSourcesResult.Result.DataSources
                        .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);
                    let allPathDataTables = getDataSourcesResult.Result.PathDataTables
                        .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);

                    setDataSources(allDataSources);
                    setPathDataTables(allPathDataTables);

                    let dataProviders1 = getDataSourcesResult.Result.DataSources.map(e => new DataProviderListDto(e.Id, e.Name + (WithDataTable ? " (DataSource)" : ""), DataProviderType.DataSource, e, null));
                    let dataProviders2 = WithDataTable ? getDataSourcesResult.Result.PathDataTables.map(e => new DataProviderListDto(e.Id, e.Name + " (DataTable)", DataProviderType.PathDataTable, null, e)) : [];

                    let allDataProviders = [...dataProviders1, ...dataProviders2];

                    allDataProviders = allDataProviders
                        .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);

                    setDataProviders(allDataProviders)

                    let selected = allDataProviders.find((e) => e.Id === dataSourceId) ?? null;

                    if (selected !== null) {
                        setDataSourceSelectionDto((previous) => { return new DataSourceSelectionDto(selected); });
                    }
                });
        }

    }, [Direction, dataSources, pathDataTables, dataSourceId, WithDataTable]);

    useEffect(() => {
        dataSourcesLoad()
    }, [dataSourcesLoad]);

    const validationRules: {
        dataSource: ValidationRule[]
    } = {
        dataSource: [
            { type: 'required', message: formatMessage("DataSourceSelectionForm_Required") },
        ]
    };

    const popupAttributes = useMemo(() => {
        return {
            id: 'elementId',
            class: 'class-name-popup'
        }
    }, []);

    const handleSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        SetDataSourceSelected(dataSourceSelectionDto);
        setIsOpen(false);
    };

    function handleDataSourceAdded(dataSourceId: number) {
        setDataSources(null);
        setDataSourceId(dataSourceId);
    }

    const createSourceButtonOptions = {
        text: formatMessage("DataSourceSelectionForm_CreateNew"),
        type: 'outlined' as ButtonType,
        useSubmitBehavior: false,
        width: '100%',
        icon: 'plus',
        onClick: createDataSource
    };

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'outlined' as ButtonType,
        useSubmitBehavior: false,
        width: '100%',
        onClick: cancel
    };

    const confirmButtonOptions = {
        text: 'Confirm',
        type: 'default' as ButtonType,
        useSubmitBehavior: true,
        width: '100%'
    };

    return (
        <React.Fragment>
            <Popup
                wrapperAttr={popupAttributes}
                visible={isOpen}
                onHiding={cancel}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title={formatMessage("DataSourceSelectionForm_Title")}
                container=".dx-viewport"
                width={600}
                height="auto"
                position={PopupPosition}
            >
                <form onSubmit={handleSubmit}>
                    <Form
                        ref={formRef}
                        formData={dataSourceSelectionDto}
                        showColonAfterLabel={true}
                        showValidationSummary={false}
                        validationGroup="dataSourceSelectionDtoData"
                        colCount={2}
                    >
                        <GroupItem colSpan={2}>
                            <SimpleItem dataField="DataProvider" editorType="dxSelectBox" editorOptions={dataSourceEditorOptions} validationRules={validationRules.dataSource}>
                                <Label render={LabelTemplate('product')} />
                            </SimpleItem>
                        </GroupItem>
                        {WithCreate && <GroupItem colSpan={2}>
                            <div style={{ width: '100%', height: 36, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 12, display: 'inline-flex' }}>
                                <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
                                    <div style={{ width: 20, height: 0, opacity: 0.50, border: '1px #5A5A65 solid' }}></div>
                                    <div style={{ color: '#9393A2', fontSize: 10, fontFamily: 'Manrope', fontWeight: '600', textTransform: 'uppercase', lineHeight: '13px', letterSpacing: 1, wordWrap: 'break-word' }}>OR</div>
                                    <div style={{ width: 20, height: 0, opacity: 0.50, border: '1px #5A5A65 solid' }}></div>
                                </div>
                            </div>
                        </GroupItem>}
                        {WithCreate && <GroupItem colSpan={2}>
                            <ButtonItem buttonOptions={createSourceButtonOptions} />
                        </GroupItem>}
                        <EmptyItem colSpan={2} />
                        <GroupItem>
                            <ButtonItem buttonOptions={cancelButtonOptions} />
                        </GroupItem>
                        <GroupItem>
                            <ButtonItem buttonOptions={confirmButtonOptions} />
                        </GroupItem>
                    </Form>
                </form>
            </Popup>
            <DataSourceForm ref={dataSourceFormHandleRef} Direction={Direction} DataSourceAdded={handleDataSourceAdded} />
        </React.Fragment>
    );
});

export default DataSourceSelectionForm;